/**
 * Wastewater data service — queries v_wastewater_inventory_summary.
 *
 * All display logic for "not yet calculated", "under review", and tier badges
 * is handled here so consuming components stay thin.
 *
 * geofips note:
 *   v_wastewater_inventory_summary stores 7-char zero-padded text, e.g. '0644000'.
 *   Frontend geofips is often numeric (e.g. 644000). Always pad before querying.
 */

import { supabaseClient } from './supabase';
import { wastewaterGeofipsVariants } from './canonicalGhgInventory/wastewaterGeofips';
import { normalizeCalculationMethodFromDb } from './canonicalGhgInventory/versionProvenance';
import {
  WASTEWATER_NOT_YET_CALCULATED,
  WASTEWATER_REVIEW_NOTE,
  WASTEWATER_TIER_DISPLAY_NOTES,
  WASTEWATER_TIER_LABELS,
  WASTEWATER_UNCALCULABLE_NOTE,
} from '../constants/methodologyNotes';

// ─── Public types ───────────────────────────────────────────────────────────

export interface WastewaterSummaryRow {
  geofips: string;
  inventory_year: number;
  jurisdiction_name: string | null;
  geography_type: string | null;
  county_name: string | null;
  methodology_version: string;
  ef_version: string;
  wastewater_emissions_mtco2e: number;
  wastewater_emissions_per_capita: number | null;
  /** CH₄ component in MTCO₂e (from v_wastewater_inventory_summary) */
  ch4_mtco2e: number | null;
  /** N₂O component in MTCO₂e (from v_wastewater_inventory_summary) */
  n2o_mtco2e: number | null;
  best_tier: number;
  worst_tier: number;
  coverage_status: string;
  review_required: boolean;
  calculation_artifact_path: string | null;
}

export interface WastewaterSummary {
  geofips: string;
  rows: WastewaterSummaryRow[];
  byYear: Map<number, WastewaterSummaryRow>;
  availableYears: number[];
  latestRow: WastewaterSummaryRow | null;
  latestYear: number | null;
  hasData: boolean;
  /** true if ANY row has review_required = true */
  reviewRequired: boolean;
  /** lowest tier number across all rows (best data quality) */
  bestTier: number | null;
  /** highest tier number across all rows (worst data quality) */
  worstTier: number | null;
}

// ─── In-memory cache ────────────────────────────────────────────────────────

const cache = new Map<string, Promise<WastewaterSummary>>();

function cacheKey(paddedGeofips: string, years?: number[]): string {
  return years ? `${paddedGeofips}:${years.sort().join(',')}` : paddedGeofips;
}

/** Clear the wastewater cache (e.g. after location change). */
export function clearWastewaterCache(): void {
  cache.clear();
}

// ─── Core fetch ─────────────────────────────────────────────────────────────

async function fetchWastewaterSummaryUncached(
  paddedGeofips: string,
  years?: number[]
): Promise<WastewaterSummary> {
  const variants = wastewaterGeofipsVariants(paddedGeofips);

  for (const gf of variants) {
    let query = supabaseClient
      .from('v_wastewater_inventory_summary')
      .select(
        'geofips,inventory_year,jurisdiction_name,geography_type,county_name,' +
          'methodology_version,ef_version,wastewater_emissions_mtco2e,' +
          'wastewater_emissions_per_capita,ch4_mtco2e,n2o_mtco2e,best_tier,worst_tier,' +
          'coverage_status,review_required,calculation_artifact_path'
      )
      .eq('geofips', gf)
      .order('inventory_year', { ascending: true });

    if (years && years.length > 0) {
      query = query.in('inventory_year', years);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[wastewaterDataService] Supabase error:', error.message);
      continue;
    }
    if (data && data.length > 0) {
      return buildSummaryFromRows(gf, data as unknown as Record<string, unknown>[]);
    }
  }

  // Pipeline repair: aggregate from wastewater_emissions when summary view join fails
  for (const gf of variants) {
    const fallback = await fetchFromEmissionsTable(gf, years);
    if (fallback) return fallback;
  }

  return emptyWastewaterSummary(paddedGeofips);
}

async function fetchFromEmissionsTable(
  geofips: string,
  years?: number[]
): Promise<WastewaterSummary | null> {
  let query = supabaseClient
    .from('wastewater_emissions')
    .select(
      'geofips,inventory_year,quantity_mtco2e,ghg,data_tier,coverage_status,review_required,methodology_version,ef_version,calculation_artifact_path'
    )
    .eq('geofips', geofips);

  if (years && years.length > 0) {
    query = query.in('inventory_year', years);
  }

  const { data, error } = await query;
  if (error || !data || data.length === 0) return null;

  const byYear = new Map<number, Record<string, unknown>[]>();
  for (const row of data as unknown as Record<string, unknown>[]) {
    const y = Number(row.inventory_year);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y)!.push(row);
  }

  const syntheticRows: Record<string, unknown>[] = [];
  byYear.forEach((rows, year) => {
    const total = rows.reduce((s, r) => s + Number(r.quantity_mtco2e ?? 0), 0);
    const ch4 = rows
      .filter((r) => r.ghg === 'CH4')
      .reduce((s, r) => s + Number(r.quantity_mtco2e ?? 0), 0);
    const n2o = rows
      .filter((r) => r.ghg === 'N2O')
      .reduce((s, r) => s + Number(r.quantity_mtco2e ?? 0), 0);
    const tiers = rows.map((r) => Number(r.data_tier ?? 5));
    syntheticRows.push({
      geofips,
      inventory_year: year,
      jurisdiction_name: null,
      geography_type: null,
      county_name: null,
      methodology_version:
        normalizeCalculationMethodFromDb(
          rows[0]?.methodology_version != null ? String(rows[0].methodology_version) : null
        ) ?? 'unknown',
      ef_version: rows[0]?.ef_version ?? 'EF-WW-1.0',
      wastewater_emissions_mtco2e: total,
      wastewater_emissions_per_capita: null,
      ch4_mtco2e: ch4 || null,
      n2o_mtco2e: n2o || null,
      best_tier: Math.min(...tiers),
      worst_tier: Math.max(...tiers),
      coverage_status: rows[0]?.coverage_status ?? 'tier_2',
      review_required: rows.some((r) => Boolean(r.review_required)),
      calculation_artifact_path: rows[0]?.calculation_artifact_path ?? null,
    });
  });

  return buildSummaryFromRows(geofips, syntheticRows);
}

function buildSummaryFromRows(
  geofips: string,
  data: Record<string, unknown>[]
): WastewaterSummary {
  const rows: WastewaterSummaryRow[] = data.map((r) => ({
    geofips: String(r.geofips ?? geofips),
    inventory_year: Number(r.inventory_year),
    jurisdiction_name: r.jurisdiction_name != null ? String(r.jurisdiction_name) : null,
    geography_type: r.geography_type != null ? String(r.geography_type) : null,
    county_name: r.county_name != null ? String(r.county_name) : null,
    methodology_version:
      normalizeCalculationMethodFromDb(
        r.methodology_version != null ? String(r.methodology_version) : null
      ) ?? 'unknown',
    ef_version: String(r.ef_version ?? 'EF-WW-1.0'),
    wastewater_emissions_mtco2e: Number(r.wastewater_emissions_mtco2e ?? 0),
    wastewater_emissions_per_capita:
      r.wastewater_emissions_per_capita != null
        ? Number(r.wastewater_emissions_per_capita)
        : null,
    ch4_mtco2e: r.ch4_mtco2e != null ? Number(r.ch4_mtco2e) : null,
    n2o_mtco2e: r.n2o_mtco2e != null ? Number(r.n2o_mtco2e) : null,
    best_tier: Number(r.best_tier ?? 5),
    worst_tier: Number(r.worst_tier ?? 5),
    coverage_status: String(r.coverage_status ?? 'tier_5'),
    review_required: Boolean(r.review_required),
    calculation_artifact_path:
      r.calculation_artifact_path != null ? String(r.calculation_artifact_path) : null,
  }));

  const sorted = [...rows].sort((a, b) => b.inventory_year - a.inventory_year);
  const latestRow = sorted[0] ?? null;
  const byYear = new Map(rows.map((r) => [r.inventory_year, r]));
  const allTiers = rows.flatMap((r) => [r.best_tier, r.worst_tier]);

  return {
    geofips,
    rows,
    byYear,
    availableYears: rows.map((r) => r.inventory_year),
    latestRow,
    latestYear: latestRow?.inventory_year ?? null,
    hasData: rows.length > 0,
    reviewRequired: rows.some((r) => r.review_required),
    bestTier: allTiers.length > 0 ? Math.min(...allTiers) : null,
    worstTier: allTiers.length > 0 ? Math.max(...allTiers) : null,
  };
}

function emptyWastewaterSummary(geofips: string): WastewaterSummary {
  return {
    geofips,
    rows: [],
    byYear: new Map(),
    availableYears: [],
    latestRow: null,
    latestYear: null,
    hasData: false,
    reviewRequired: false,
    bestTier: null,
    worstTier: null,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Fetch and cache wastewater summary rows.
 *
 * @param geofips  Numeric or string geofips (padded to 7 chars automatically)
 * @param years    Optional year filter — if omitted, all available years are returned
 */
export async function fetchWastewaterSummary(
  geofips: number | string,
  years?: number[]
): Promise<WastewaterSummary> {
  const variants = wastewaterGeofipsVariants(geofips);
  const primary = variants[0] ?? String(geofips).padStart(7, '0');
  const key = cacheKey(primary, years);
  if (cache.has(key)) return cache.get(key)!;
  const promise = fetchWastewaterSummaryUncached(primary, years);
  cache.set(key, promise);
  return promise;
}

/**
 * Get the row for a specific inventory year, or null if not yet calculated.
 * Never falls back to another year.
 */
export function getWastewaterRowForYear(
  summary: WastewaterSummary,
  year: number
): WastewaterSummaryRow | null {
  return summary.byYear.get(year) ?? null;
}

/**
 * Human-readable tier label for display in tooltips, badges, and data-quality sections.
 */
export function formatWastewaterTierLabel(row: WastewaterSummaryRow | null): string {
  if (!row) return WASTEWATER_NOT_YET_CALCULATED;
  if (row.coverage_status === 'uncalculable') return WASTEWATER_UNCALCULABLE_NOTE;
  return WASTEWATER_TIER_LABELS[row.best_tier] ?? `Tier ${row.best_tier}`;
}

/**
 * Returns true if the row represents a low-confidence estimate that warrants
 * a badge or visual callout (Tier 5 or review_required).
 */
export function shouldShowWastewaterLowConfidence(row: WastewaterSummaryRow | null): boolean {
  if (!row) return false;
  return row.worst_tier >= 5 || row.review_required;
}

/**
 * Returns a human-readable data-quality note for UI display (callouts, tooltips, reports).
 *
 * Priority order:
 *   1. uncalculable   → "Insufficient information is available to estimate wastewater emissions."
 *   2. Tier 5         → "Estimate based on statewide wastewater assumptions. Lower confidence."
 *   3. Tier 4         → "Estimate based on attributed wastewater service."
 *   4. review_required (Tier 1–3 only) → "Estimate under review."
 *   5. Tier 1–2–3, no review flag → null (no callout needed)
 *
 * Rules:
 *   - Never use "unreliable", "poor quality", or "inaccurate".
 *   - Always communicate confidence level, not correctness.
 */
export function getWastewaterDisplayNote(row: WastewaterSummaryRow | null): string | null {
  if (!row) return null;
  if (row.coverage_status === 'uncalculable') return WASTEWATER_UNCALCULABLE_NOTE;
  const tierNote = WASTEWATER_TIER_DISPLAY_NOTES[row.worst_tier];
  if (tierNote != null) return tierNote;
  // Tier 1–3: only surface a note if marked for review (should be rare)
  if (row.review_required) return WASTEWATER_REVIEW_NOTE;
  return null;
}
